import type { ForwardedRef, ForwardRefExoticComponent } from 'react';
import { forwardRef, useContext } from 'react';
import styled from '@emotion/styled';
import type { PropsOf } from '@emotion/react';
import { css } from '@emotion/react';
import * as tokens from '@tokens';
import { Icon } from '@components/Icon/Icon';
import type { IconName } from '@components/Icon/BaseIcon';
import { Loader } from '@components/Loader/Loader';
import type {
  ButtonOrLinkProps,
  ButtonOrLinkElement,
} from '@common/ButtonOrLink';
import { externalRel } from '@common/ButtonOrLink';
import { navigationClickHandler } from '@utils/navigation-handler';
import { shouldForwardProp } from '@utils/styledProps';
import { HotsauceContext } from '@providers/hotsauceContext';
import {
  baseStyles,
  primaryStyles,
  secondaryStyles,
  ghostStyles,
  ghostStandoutStyles,
  dangerStyles,
  dangerSecondaryStyles,
} from './styles';
import { useStrings } from '@hooks/useStrings';

export type ButtonKind =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'ghost_standout'
  | 'danger'
  | 'danger_secondary';

export type ButtonProps<T extends ButtonOrLinkElement = ButtonOrLinkElement> =
  ButtonOrLinkProps<T> & {
    /**
     * Kind of button used.
     */
    kind?: ButtonKind;
    /**
     * size of the button
     */
    size?: 's' | 'm' | 'l';
    /**
     * Icon to be shown in the button. Will always show on the left.
     */
    icon?: IconName;
    /**
     * Text content of the button.
     */
    children: string;
    /**
     * Should the button returns a loading state. Only applies when `as="button"`
     */
    isLoading?: T extends 'button' ? boolean : undefined;
    /**
     * Override text to show when in loading state. Only applies when
     * `as="button"`
     */
    loadingText?: T extends 'button' ? string : undefined;
    /**
     * Styles override.
     */
    className?: string;
  };

const BUTTON_HEIGHT = {
  s: 32,
  m: 40,
  l: 48,
};

const BUTTON_FONT_SIZE = {
  s: tokens.text('subtitle2'),
  m: tokens.text('subtitle2'),
  l: tokens.text('subtitle1'),
};

const BUTTON_KIND_STYLES = {
  primary: primaryStyles,
  secondary: secondaryStyles,
  ghost: ghostStyles,
  ghost_standout: ghostStandoutStyles,
  danger: dangerStyles,
  danger_secondary: dangerSecondaryStyles,
};

const getDisabledBackgroundColor = ({ kind }: { kind: ButtonKind }) => {
  if (kind.startsWith('ghost')) {
    return 'transparent';
  }

  return tokens.color.ui_disabled;
};

/**
 * List of props to use in the styled component
 * @private
 */
const styledButtonPropNames = ['kind', 'size', 'isLoading'] as const;

const StyledButton = styled('button', {
  shouldForwardProp: shouldForwardProp(styledButtonPropNames),
})<Omit<ButtonProps<'button'>, 'as' | 'children'>>`
  ${baseStyles};
  height: ${({ size }) => BUTTON_HEIGHT[size]}px;
  min-height: ${({ size }) => BUTTON_HEIGHT[size]}px;
  ${({ size }) => BUTTON_FONT_SIZE[size]};

  ${({ kind }) => BUTTON_KIND_STYLES[kind]};

  &[disabled] {
    cursor: ${({ isLoading }) => (isLoading ? 'progress' : 'not-allowed')};

    background-color: ${({ kind }) => getDisabledBackgroundColor({ kind })};

    ${tokens.elevation(0)};
    color: ${({ isLoading }) =>
      isLoading ? tokens.color.text_default : tokens.color.text_disabled};
  }
`;

const StyledLink = styled('a', {
  shouldForwardProp: shouldForwardProp(styledButtonPropNames),
})<Omit<ButtonProps<'a'>, 'as' | 'children'>>`
  ${baseStyles};
  text-decoration: none;
  height: ${({ size }) => BUTTON_HEIGHT[size]}px;

  &:hover,
  &:focus,
  &:active {
    text-decoration: none;
  }

  ${({ kind }) => BUTTON_KIND_STYLES[kind]};
`;

const ButtonIcon = ({
  icon,
}: Pick<ButtonProps, 'icon'>): JSX.Element | null => {
  if (!icon) {
    return null;
  }

  return (
    <Icon
      icon={icon}
      size="m"
      css={css`
        margin-right: ${tokens.spacer.x1};
      `}
    />
  );
};

const ButtonLoader = (
  <Loader
    isGreyscale
    css={css`
      margin-right: ${tokens.spacer.x1};
    `}
  />
);

type PassedButtonProps<T extends ButtonOrLinkElement> = Omit<
  ButtonProps<T>,
  'as' | 'children' | 'icon'
>;

/**
 * Render function for Button component
 * @see Button
 * @private
 */
const ButtonRender = <T extends ButtonOrLinkElement = ButtonOrLinkElement>(
  { as, children, icon, ...props }: ButtonProps<T>,
  ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>,
): JSX.Element => {
  const { onNavigate } = useContext(HotsauceContext) ?? {};
  const { actions } = useStrings();

  // link case
  if (as === 'a') {
    const { isExternal, rel, ...linkProps } = props as PassedButtonProps<'a'>;
    const onClick = (event) =>
      navigationClickHandler({
        event,
        isExternal,
        linkProps: linkProps as PropsOf<'a'>,
        onNavigate,
      });

    return (
      <StyledLink
        {...linkProps}
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        rel={isExternal ? externalRel(rel) : undefined}
        target={isExternal ? '_blank' : undefined}
        onClick={onClick}
      >
        <ButtonIcon icon={icon} />
        <span>{children}</span>
      </StyledLink>
    );
  }

  const { isDisabled, isLoading, loadingText, type, ...buttonProps } =
    props as PassedButtonProps<'button'>;

  return (
    <StyledButton
      {...buttonProps}
      ref={ref as ForwardedRef<HTMLButtonElement>}
      type={type || 'button'}
      disabled={isDisabled || isLoading}
      isLoading={isLoading}
    >
      {isLoading ? ButtonLoader : <ButtonIcon icon={icon} />}
      {isLoading ? loadingText || actions.loading : <span>{children}</span>}
    </StyledButton>
  );
};

/**
 * Clickable elements used to perform actions.
 *
 * {@link https://zeroheight.com/76c439891/p/48d21c-button/b/734439 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-button--basic-button Code documentation}
 *
 * @example
 * <Button>Button</Button>
 */
export const Button = forwardRef(
  ButtonRender,
) as ForwardRefExoticComponent<ButtonProps>;

Button.defaultProps = {
  as: 'button',
  kind: 'primary',
  size: 'm',
};

Button.displayName = 'Button';
